import type {
  ClientExceptionEntity,
  GetAllPostsByAppVariantAndSelectedGeoAreasRequest,
  GetMeinOrtPostsRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { ClientExceptionEntityTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type {
  LegendListRef,
  LegendListRenderItemProps
} from '@legendapp/list/react-native';
import { AnimatedLegendList } from '@legendapp/list/reanimated';
import { useScrollToTop } from '@react-navigation/native';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import type { SharedValue } from 'react-native-reanimated';

import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import { useGetAllPostsBySelectedGeoAreas } from '@/features/api/hooks/grapevine-post/useGetAllPostsBySelectedGeoAreas';
import { FeedStateView } from '@/features/posts/components/feed/FeedStateView';
import { PostScreenFeedCard } from '@/features/posts/components/feed/PostScreenCards';
import { useTrackFeedPostViews } from '@/features/posts/hooks/useTrackFeedPostViews';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ErrorStateView } from '@/shared/components/state-view/ErrorStateView';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type PostFeedContextValue = {
  query: UseInfiniteQueryResult<GenericPostDetail[], ClientExceptionEntity>;
  posts: GenericPostDetail[] | undefined;
};

const PostFeedContext = createContext<PostFeedContextValue | undefined>(
  undefined
);

const usePostFeed = () => {
  const context = useContext(PostFeedContext);
  if (!context) {
    throw new Error('PostFeed slots must be used within PostFeed');
  }
  return context;
};

const makeStyles = () =>
  StyleSheet.create({
    list: { flex: 1 },
    feed: { marginHorizontal: 12, marginTop: 12, flexGrow: 1 },
    center: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      elevation: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 32
    }
  });

export const PostFeed = ({
  params,
  query: injectedQuery,
  enabled = true,
  children
}: PropsWithChildren<{
  params:
    | GetAllPostsByAppVariantAndSelectedGeoAreasRequest
    | GetMeinOrtPostsRequest;
  query?: UseInfiniteQueryResult<GenericPostDetail[], ClientExceptionEntity>;
  enabled?: boolean;
}>) => {
  const defaultQuery = useGetAllPostsBySelectedGeoAreas(
    params,
    enabled && !injectedQuery
  );
  const query = injectedQuery ?? defaultQuery;
  const value = useMemo(() => ({ query, posts: query.data }), [query]);
  return (
    <PostFeedContext.Provider value={value}>
      {children}
    </PostFeedContext.Provider>
  );
};

const List = forwardRef<
  LegendListRef,
  {
    hideEndView?: boolean;
    hidePostType?: boolean;
    contentContainerStyle?: object;
    scrollOffset?: SharedValue<number>;
  }
>(
  (
    { hideEndView = false, hidePostType, contentContainerStyle, scrollOffset },
    forwardedRef
  ) => {
    const styles = useMemo(() => makeStyles(), []);
    const { query, posts: rawPosts } = usePostFeed();
    const isPlaceholder = query.isPlaceholderData;
    const posts = useMemo(() => {
      if (isPlaceholder) {
        return [];
      }
      const seen = new Set<string>();
      return (rawPosts ?? []).filter((post) => {
        if (post.postId == undefined) {
          return true;
        }
        if (seen.has(post.postId)) {
          return false;
        }
        seen.add(post.postId);
        return true;
      });
    }, [isPlaceholder, rawPosts]);
    const { fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = query;

    const renderPost = useCallback(
      (
        post: LegendListRenderItemProps<GenericPostDetail, string | undefined>
      ) => <PostScreenFeedCard post={post.item} hidePostType={hidePostType} />,
      [hidePostType]
    );

    const viewabilityConfigCallbackPairs =
      useTrackFeedPostViews<GenericPostDetail>();

    const getItemType = useCallback(
      (item: GenericPostDetail) =>
        (item.images?.length ?? 0) > 0 ? 'with-image' : 'text-only',
      []
    );

    const listRef = useRef<LegendListRef>(null);

    const setListRef = useCallback(
      (instance: LegendListRef | null) => {
        listRef.current = instance;
        if (typeof forwardedRef === 'function') {
          forwardedRef(instance);
        } else if (forwardedRef) {
          forwardedRef.current = instance;
        }
      },
      [forwardedRef]
    );
    useScrollToTop(listRef);

    const sharedValues = useMemo(
      () => (scrollOffset ? { scrollOffset } : undefined),
      [scrollOffset]
    );

    const [isRefreshing, setIsRefreshing] = useState(false);
    const onRefresh = useCallback(() => {
      setIsRefreshing(true);
      void refetch()
        .catch(() => {})
        .finally(() => setIsRefreshing(false));
    }, [refetch]);

    return (
      <AnimatedLegendList<GenericPostDetail>
        ref={setListRef}
        style={styles.list}
        testID="post-feed-list"
        data={posts}
        maintainVisibleContentPosition={false}
        refreshControl={
          <Refresher refreshing={isRefreshing} onRefresh={onRefresh} />
        }
        renderItem={renderPost}
        drawDistance={1200}
        recycleItems
        getItemType={getItemType}
        sharedValues={sharedValues}
        keyExtractor={(item, idx) => item.postId ?? idx.toString()}
        ListEmptyComponent={
          <View>
            {!hasItems(posts) &&
              !query.isLoading &&
              !hasNextPage &&
              !query.isPlaceholderData && <FeedStateView state="empty" />}
          </View>
        }
        ListFooterComponent={
          <View>
            {isFetchingNextPage && <ActivityIndicator size={80} />}
            {!hasNextPage && !hideEndView && hasItems(posts) && (
              <FeedStateView />
            )}
          </View>
        }
        extraData={[isFetchingNextPage, hasNextPage, hideEndView]}
        onEndReached={() =>
          hasNextPage && !isFetchingNextPage && fetchNextPage()
        }
        onEndReachedThreshold={0.8}
        contentContainerStyle={[styles.feed, contentContainerStyle]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs}
      />
    );
  }
);

List.displayName = 'PostFeed.List';

const Loading = () => {
  const styles = useMemo(() => makeStyles(), []);
  const theme = useAppTheme();
  const { query } = usePostFeed();
  if (!query.isLoading) {
    return <></>;
  }
  return (
    <View
      style={[
        styles.center,
        { backgroundColor: theme.colors.surface },
        { pointerEvents: 'box-none' }
      ]}
    >
      <ActivityIndicator size={48} />
    </View>
  );
};

const ErrorView = () => {
  const styles = useMemo(() => makeStyles(), []);
  const theme = useAppTheme();
  const { query } = usePostFeed();
  const { refetch } = query;
  const errorType = getClientErrorType(query.error);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-feed.state.error'
  });

  if (!query.isError) {
    return <></>;
  }

  const isHomeAreaRequired =
    errorType === ClientExceptionEntityTypeEnum.HomeAreaRequired;

  return (
    <View style={[styles.center, { backgroundColor: theme.colors.surface }]}>
      <ErrorStateView
        onRetry={() => void refetch()}
        title={isHomeAreaRequired ? t('homeAreaRequired.title') : undefined}
        subtitle={
          isHomeAreaRequired ? t('homeAreaRequired.subtitle') : undefined
        }
      />
    </View>
  );
};

PostFeed.Loading = Loading;
PostFeed.Error = ErrorView;
PostFeed.List = List;
